import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import { COUNTRY_CODES, countryName } from '@/lib/countries'
import { cn } from '@/lib/utils'

export interface CountryPickerProps {
  /** The ISO-2 code, or empty for none. */
  value: string
  onChange: (code: string) => void
  id?: string
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  'aria-describedby'?: string
}

/**
 * Choosing a country by its name, stored as its two-letter code.
 *
 * <p>Replaces a free-text box with the hint "two-letter country code". People typed what a
 * nationality is called — INDIAN, Filipino, UAE — and the server refused every one of them
 * with "size must be between 0 and 2". Searching by name and storing the code means nobody has
 * to know the code at all, and the record never holds a spelling that a report cannot group.
 */
export function CountryPicker({
  value, onChange, id, placeholder = 'Choose a country…', disabled, invalid, ...aria
}: CountryPickerProps) {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const countries = useMemo(
    () => COUNTRY_CODES
      .map((code) => ({ code, name: countryName(code, i18n.language) }))
      .sort((a, b) => a.name.localeCompare(b.name, i18n.language)),
    [i18n.language],
  )

  const needle = term.trim().toLowerCase()
  const shown = needle
    ? countries.filter((country) =>
      country.name.toLowerCase().includes(needle) || country.code.toLowerCase() === needle)
    : countries

  const label = value ? countryName(value, i18n.language) : null

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        aria-describedby={aria['aria-describedby']}
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-[var(--radius-control)] border bg-[var(--color-card)] px-3 text-start text-sm text-[var(--color-heading)] disabled:cursor-not-allowed disabled:opacity-50',
          invalid ? 'border-[var(--color-danger)]' : 'border-[var(--color-border)]',
        )}
      >
        <span className={label ? '' : 'text-[var(--color-muted)]'}>
          {label ? `${label} (${value})` : placeholder}
        </span>
        <span className="flex items-center gap-1">
          {value && !disabled && (
            <X
              className="size-4 text-[var(--color-muted)]"
              aria-hidden="true"
              onClick={(event) => { event.stopPropagation(); onChange('') }}
            />
          )}
          <ChevronDown className="size-4 text-[var(--color-muted)]" aria-hidden="true" />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] shadow-lg">
          <div className="relative border-b border-[var(--color-border)] p-2">
            <Search
              className="pointer-events-none absolute start-4 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]"
              aria-hidden="true"
            />
            <input
              autoFocus
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Type a country"
              aria-label="Search for a country"
              className="h-9 w-full rounded-[var(--radius-control)] bg-[var(--color-page)] ps-8 pe-2 text-sm outline-none"
            />
          </div>
          <ul role="listbox" className="max-h-64 overflow-y-auto p-1">
            {shown.length === 0 && (
              <li className="p-3 text-sm text-[var(--color-muted)]">No country matches “{term}”.</li>
            )}
            {shown.map((country) => (
              <li key={country.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={country.code === value}
                  onClick={() => { onChange(country.code); setOpen(false); setTerm('') }}
                  className="flex w-full items-center gap-2 rounded-[var(--radius-control)] p-2 text-start text-sm hover:bg-[var(--color-page)]"
                >
                  <Check
                    className={cn('size-4 shrink-0', country.code === value ? 'opacity-100' : 'opacity-0')}
                    aria-hidden="true"
                  />
                  <span className="flex-1">{country.name}</span>
                  <span className="font-mono text-xs text-[var(--color-muted)]">{country.code}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
